import { useEffect, useRef, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import { PartyPopper } from 'lucide-react';
import { Button } from '@tailoring-crm/ui';

/**
 * The first order is booked — the moment the whole tour was walking towards, so it is marked.
 *
 * Deliberately NOT the app's `<Dialog>`: a modal dialog sits in the browser's top layer, above
 * every z-index, and the confetti would fall behind its backdrop. This is a plain fixed layer
 * with the confetti canvas above it. It still behaves like a dialog — focus lands on it, Escape
 * closes it — and the confetti is skipped for anyone who has asked for less motion.
 */
export function TourCelebration({
  setupFinished,
  onClose,
  onSetup,
}: {
  setupFinished: boolean;
  onClose: () => void;
  onSetup: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const primary = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primary.current?.focus();
    const burst = (options: confetti.Options) =>
      void confetti({ zIndex: 70, disableForReducedMotion: true, ...options });
    burst({ particleCount: 120, spread: 80, origin: { y: 0.55 } });
    const sides = window.setTimeout(() => {
      burst({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0, y: 0.7 } });
      burst({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.7 } });
    }, 250);
    return () => {
      window.clearTimeout(sides);
      confetti.reset();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground/40 p-4 animate-in fade-in-0 motion-reduce:animate-none sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-celebration-title"
        className="w-full max-w-sm rounded-2xl bg-background p-6 text-center shadow-2xl animate-in zoom-in-90 fade-in-0 duration-500 motion-reduce:animate-none"
      >
        <span
          aria-hidden="true"
          className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground motion-safe:animate-bounce"
        >
          <PartyPopper className="size-7" />
        </span>
        <h2 id="tour-celebration-title" className="mt-4 text-xl font-semibold">
          {t('onboarding.spotlight.finish.title')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t(
            setupFinished
              ? 'onboarding.spotlight.finish.descriptionDone'
              : 'onboarding.spotlight.finish.description',
          )}
        </p>
        <div className="mt-5 flex flex-col gap-2">
          {setupFinished ? (
            <Button ref={primary} onClick={onClose}>
              {t('onboarding.spotlight.finish.done')}
            </Button>
          ) : (
            <>
              <Button ref={primary} onClick={onSetup}>
                {t('onboarding.spotlight.finish.setup')}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                {t('onboarding.spotlight.finish.done')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
